import { gql } from '@apollo/client';
import { ClaimFlowType } from 'types';

import useSWR from 'hooks/useSWR';

import { fetchPayAnyoneShowResponse } from 'utils/graphql/fetchPayAnyoneShowResponse';

import { useGQLQuery } from './useGQLQuery';

type useTokenInfoResponse = {
  data?: any;
  error?: any;
  isLoading: boolean;
  isError: boolean;
  refetch?: () => void;
};

export const PAY_ANYONE_TOKEN_RESPONSE_QUERY = gql`
  query PayAnyoneByTokenResponse($token: String!) {
    unauthenticated {
      p2p {
        pay_anyone_client_config {
          maintenance_mode_enabled
          ocr_enabled
          graphql_usage {
            use_gql_get_and_claim_pay_anyone
          }
        }
        pay_anyone(token: $token) {
          __typename
          ... on PayAnyoneWithExternalCard {
            claim_with_chime
            pay_friend {
              id
              ux_status
              amount
              memo
              memo_gif_id
              expires_at
              public_id
              receiver_identifier
            }
            previous_claim {
              external_card_id
              first_name
              last_four
            }
            recipient_previously_claimed
            sender {
              bank
              id
              referral_token
              name
            }
          }
          ... on PayAnyoneFetchError {
            error_id
            error_header
            error_body
          }
          ... on StepUpRequiredError {
            title
            message
            challenge_context
          }
        }
      }
    }
  }
`;

function useTokenInfo(
  token: string | string[],
  claimFlowType: ClaimFlowType
): useTokenInfoResponse {
  const isPayAnyoneFlow = claimFlowType === ClaimFlowType.pay;

  const fetchPath = isPayAnyoneFlow ? 'pf' : 'mr';
  const url =
    token && `${process.env.CONSUMER_HOST}/${fetchPath}/${token}.json`;
  const { data, error } = useSWR(url || null);

  const {
    data: graphqlData,
    error: graphqlError,
    loading: graphqlLoading,
    refetch: refetch,
  } = useGQLQuery(PAY_ANYONE_TOKEN_RESPONSE_QUERY, {
    fetchPolicy: 'no-cache',
    variables: { token: token },
  });
  const { use_gql_get_and_claim_pay_anyone } =
    graphqlData?.unauthenticated?.p2p?.pay_anyone_client_config
      ?.graphql_usage || {};

  if (use_gql_get_and_claim_pay_anyone && isPayAnyoneFlow) {
    return fetchPayAnyoneShowResponse(
      graphqlData,
      graphqlError,
      graphqlLoading,
      refetch
    );
  } else {
    return {
      data,
      error,
      isLoading: !error && !data,
      isError: !!error,
    };
  }
}

export default useTokenInfo;
